import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/forms/FormField';
import { RatingPicker } from '@/components/forms/RatingPicker';
import { toast } from '@/components/ui/Toast';
import { allocationsApi, stockApi } from '@/features/stock/api';
// PRD §9 v2 — Filter the picker by the event's eligible prize pool.
import { eventPrizePoolApi } from '@/features/events/eventPrizePoolApi';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import { useAuth } from '@/lib/auth/useAuth';
import type { XkStationSummary, XkStock } from '@/lib/api/types';

interface InventoryTabProps {
  summary: XkStationSummary;
}

export function InventoryTab({ summary }: InventoryTabProps) {
  const t = useT();
  const queryClient = useQueryClient();
  const { companyId: userCompanyId } = useAuth();
  const stationId = summary.station.id;

  // Stocks available to allocate: union of station's company and the user's
  // workspace (parent brand can register stock at the workspace level and
  // child brands inherit it). Same logic as the legacy page.
  const stockCompanyIds = (() => {
    const ids = new Set<number>();
    if (summary.station.companyId) ids.add(summary.station.companyId);
    if (userCompanyId && userCompanyId !== summary.station.companyId) {
      ids.add(userCompanyId);
    }
    return [...ids];
  })();
  const stocks = useQuery({
    queryKey: queryKeys.stock.byBrand(summary.station.companyId ?? 0),
    queryFn: () =>
      stockApi.list({
        companyId:
          stockCompanyIds.length === 1 ? stockCompanyIds[0] : stockCompanyIds,
      }),
    enabled: !!summary.station.companyId,
  });

  const [stockId, setStockId] = useState<number | ''>('');
  const [quantity, setQuantity] = useState(10);
  const [rating, setRating] = useState<number | null>(null);

  const invalidateSummary = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.stations.summary(stationId),
    });

  const create = useMutation({
    mutationFn: () => {
      if (!stockId) throw new Error(t('stations.detail.missingData'));
      return allocationsApi.create({
        stationId,
        eventId: summary.station.eventId,
        companyId: summary.station.companyId,
        stockId: Number(stockId),
        quantity,
        rating: rating ?? null,
        position: summary.inventory.length,
      });
    },
    onSuccess: () => {
      invalidateSummary();
      setStockId('');
      setQuantity(10);
      setRating(null);
      toast({ tone: 'success', title: t('stations.detail.allocatedToast') });
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('stations.detail.allocateFailed'),
        description: (e as Error).message,
      }),
  });

  const remove = useMutation({
    mutationFn: (allocId: number) => allocationsApi.remove(allocId),
    onSuccess: invalidateSummary,
  });

  // PRD §9 v2 — Per-event prize pool. If the event admin curated a list of
  // eligible prizes, the picker only shows that subset. Empty pool ==
  // back-compat: any brand-active stock is allowed.
  const eventPoolQ = useQuery({
    queryKey: ['event-prize-pool', summary.station.eventId],
    queryFn: () => eventPrizePoolApi.list(summary.station.eventId),
    enabled: !!summary.station.eventId,
  });
  const eligibleStockIds = new Set(
    (eventPoolQ.data?.items ?? [])
      .filter((p) => p.status === 'Enable')
      .map((p) => p.stockId),
  );

  // NOTE: we intentionally do NOT filter out already-allocated stocks. The
  // same Xotski type may be allocated to a station MULTIPLE times — so that
  // when there are few prize types a single type can occupy several wheel
  // segments and its win probability can be tuned per slice. The brand pool
  // is still enforced (per-station headroom hint below + server-side
  // getBrandHeadroom on create, which sums all active allocations).
  const availableStocks =
    stocks.data?.items.filter((s) => {
      if (s.status !== 'Enable') return false;
      // PRD §9 — only stocks with brand-level units left (legacy NULL rows
      // are uncapped — keep them visible).
      if (s.quantityRemaining != null && s.quantityRemaining <= 0) return false;
      // PRD §9 v2 — if pool is curated, gate against it; otherwise allow all.
      if (eligibleStockIds.size > 0 && !eligibleStockIds.has(s.id)) {
        return false;
      }
      return true;
    }) ?? [];

  // PRD §9 — Surface the brand-level pool next to the picker so operators
  // see how many units are still in the brand's possession before they
  // commit a station allocation. The server is the source of truth for the
  // real headroom (it subtracts other stations' active allocations) — this
  // value is the upper-bound. A server-side cap rejection surfaces via the
  // toast on `create.onError`.
  const selectedStock: XkStock | undefined =
    stockId === '' ? undefined : stocks.data?.items.find((s) => s.id === stockId);
  // Units of the selected stock already earmarked at THIS station (summed
  // across all of its allocation rows). Because the same type can now be
  // allocated more than once, subtract what's already committed here so the
  // picker reflects the real room left before the server rejects an
  // over-allocation. The server (getBrandHeadroom) additionally nets out
  // OTHER stations, so this remains an upper bound — a server cap rejection
  // still surfaces via the toast on create.onError.
  const allocatedHereForStock =
    stockId === ''
      ? 0
      : summary.inventory
          .filter((a) => a.stockId === stockId)
          .reduce((sum, a) => sum + a.quantity, 0);
  const brandRemaining =
    selectedStock?.quantityRemaining == null
      ? null
      : Math.max(0, selectedStock.quantityRemaining - allocatedHereForStock);
  const quantityExceedsPool =
    brandRemaining != null && quantity > brandRemaining;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('stations.detail.allocationsTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 sm:grid-cols-[2fr_1fr_auto_auto]">
          <FormField
            label={t('stations.detail.stock')}
            required
            hint={
              stocks.isLoading
                ? t('stations.detail.loadingStocks')
                : (stocks.data?.items.length ?? 0) === 0
                  ? t('stations.detail.noStocksForBrand')
                  : availableStocks.length === 0
                    ? t('stations.detail.allAllocated')
                    : `${availableStocks.length} ${t('stations.detail.available')}`
            }
          >
            {!stocks.isLoading && (stocks.data?.items.length ?? 0) === 0 ? (
              <Link
                to="/stock"
                className="inline-flex h-10 items-center justify-center rounded-md border border-dashed border-[var(--color-border-default)] px-3 text-sm font-medium text-[var(--color-accent)] hover:bg-[var(--color-bg-surface)]"
              >
                {t('stations.detail.createStocksCta')}
              </Link>
            ) : (
              <Select
                value={stockId}
                onChange={(e) =>
                  setStockId(e.target.value ? Number(e.target.value) : '')
                }
                disabled={availableStocks.length === 0}
              >
                <option value="">{t('stations.detail.pickStock')}</option>
                {availableStocks.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField
            label={t('stations.detail.quantity')}
            required
            hint={
              brandRemaining != null
                ? `${t('stations.detail.brandPool')}: ${brandRemaining}`
                : undefined
            }
            error={
              !Number.isFinite(quantity) || quantity <= 0
                ? t('stations.detail.qtyError')
                : quantityExceedsPool
                  ? `${t('stations.detail.exceedsBrandPool')} (${brandRemaining})`
                  : undefined
            }
          >
            <Input
              type="number"
              min={1}
              step={1}
              max={brandRemaining ?? undefined}
              value={quantity}
              onChange={(e) =>
                setQuantity(Math.max(0, parseInt(e.target.value || '0', 10)))
              }
            />
          </FormField>
          <FormField
            label={t('stations.detail.overrideRating')}
            hint={
              rating == null
                ? t('stations.detail.usesDefault')
                : `${t('stations.detail.wheelWeight')} ${(6 - rating) ** 2}`
            }
          >
            <RatingPicker value={rating ?? 0} onChange={setRating} />
          </FormField>
          <div className="flex items-end">
            <Button
              disabled={
                !stockId ||
                quantity <= 0 ||
                !Number.isFinite(quantity) ||
                quantityExceedsPool
              }
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              {t('stations.detail.allocate')}
            </Button>
          </div>
        </div>

        {summary.inventory.length === 0 ? (
          <p className="text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.noAllocations')}
          </p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
                <tr>
                  <th className="py-2 text-left">{t('stations.detail.stockCol')}</th>
                  <th className="py-2 text-left">{t('stations.detail.ratingCol')}</th>
                  <th className="py-2 text-right">{t('stations.detail.usedCol')}</th>
                  <th className="py-2 text-right">{t('stations.detail.availTotalCol')}</th>
                  <th className="py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {summary.inventory.map((a) => (
                  <tr
                    key={a.id}
                    className="border-t border-[var(--color-border-subtle)]"
                  >
                    <td className="py-3 font-semibold">{a.stockName}</td>
                    <td className="py-3">
                      <RatingPicker readOnly size="sm" value={a.rating ?? 0} />
                    </td>
                    <td className="py-3 text-right tabular-nums">{a.used}</td>
                    <td className="py-3 text-right tabular-nums">
                      {a.available}
                      <span className="text-[var(--color-fg-subtle)]">
                        {' '}
                        / {a.quantity}
                      </span>
                      {a.available === 0 && (
                        <Badge tone="warn" className="ml-2">
                          {t('stations.detail.out')}
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove.mutate(a.id)}
                        aria-label={t('stations.detail.removeAria')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-[var(--color-border-default)]">
                  <td className="py-3 text-sm uppercase text-[var(--color-fg-muted)]" colSpan={2}>
                    {t('stations.detail.totalAllocated')}
                  </td>
                  <td className="py-3 text-right tabular-nums font-semibold">
                    {summary.inventoryTotals.used}
                  </td>
                  <td className="py-3 text-right tabular-nums font-semibold">
                    {summary.inventoryTotals.available}
                    <span className="text-[var(--color-fg-subtle)]">
                      {' '}
                      / {summary.inventoryTotals.quantity}
                    </span>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </CardContent>
    </Card>
  );
}
